import { fetcher } from '../../../lib/api-fetcher';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

export const userKeys = {
  all: ['users'] as const,
  lists: (filters: Record<string, unknown>) => [...userKeys.all, 'list', filters] as const,
  details: (id: string) => [...userKeys.all, 'detail', id] as const,
};

export interface UserListRow {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string;
  status: string;
  createdAt: string;
  isStaff: boolean;
  isAgent: boolean;
  isCustomer: boolean;
  staffType: string | null;
  roles: Array<{ id: string; name: string; companyWide?: boolean }>;
  orgUnits: Array<{ id: string; name: string }>;
}

export interface UserDetail {
  id: string;
  name: string;
  email: string | null;
  phoneNumber: string;
  status: string;
  createdAt: string;
  mustChangePassword: boolean;
  mustResetMpin: boolean;
  staffProfile: {
    fullName: string;
    type: string;
  } | null;
  agentProfile: {
    fullName: string;
    supervisorId: string | null;
    supervisorName: string | null;
  } | null;
  customerProfile: {
    fullName: string;
    kycStatus: string;
  } | null;
  roles: Array<{ id: string; name: string; type: string }>;
  orgUnits: Array<{ id: string; name: string; role: string | null }>;
  companyWide: boolean;
}

export interface UserListFilters {
  page: number;
  limit: number;
  type?: 'staff' | 'agent' | 'customer';
  status?: string;
  roleId?: string;
  search?: string;
}

export function useUsersList(filters: UserListFilters) {
  return useQuery({
    queryKey: userKeys.lists(filters as unknown as Record<string, unknown>),
    queryFn: async () => {
      // Build search params dynamically
      const params = new URLSearchParams();
      params.append('page', String(filters.page));
      params.append('limit', String(filters.limit));
      if (filters.type) params.append('type', filters.type);
      if (filters.status) params.append('status', filters.status);
      if (filters.roleId) params.append('roleId', filters.roleId);
      if (filters.search) params.append('search', filters.search);

      return fetcher.get<{ data: UserListRow[]; meta: { total: number; page: number; limit: number } }>(
        `/staff/v1/users?${params.toString()}`
      );
    },
  });
}

export function useUserDetail(id: string) {
  return useQuery({
    queryKey: userKeys.details(id),
    queryFn: async () => {
      return fetcher.get<UserDetail>(`/staff/v1/users/${id}`);
    },
    enabled: !!id,
  });
}

export function useAssignUserRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, roleId, companyWide }: { userId: string; roleId: string; companyWide?: boolean }) =>
      fetcher.post<unknown>(`/staff/v1/users/${userId}/roles`, { roleId, companyWide }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useGrantAgentAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgUnitId,
      supervisorId,
    }: {
      userId: string;
      orgUnitId: string;
      supervisorId: string | null;
    }) =>
      fetcher.post<unknown>(`/staff/v1/users/${userId}/grant-agent`, { orgUnitId, supervisorId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}

export function useGrantStaffAccess() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      orgUnitId,
      roleId,
      companyWide,
    }: {
      userId: string;
      orgUnitId: string;
      roleId: string;
      companyWide?: boolean;
    }) =>
      fetcher.post<unknown>(`/staff/v1/users/${userId}/grant-staff`, { orgUnitId, roleId, companyWide }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: userKeys.all });
    },
  });
}
